// Researcher / University — Figma frames 85:3, 86:2, 93:2, 87:2

export const RESEARCHER_NAV = [
  { label: 'Overview',            to: '/researcher' },
  { label: 'Research Activities', to: '/researcher/activities' },
  { label: 'Evidence',            to: '/researcher/evidence' },
  { label: 'Collaborate',         to: '/researcher/collaborate' },
];

export const RESEARCHER_CTA = {
  primary:   { label: 'Request Collaboration', to: '/researcher/collaborate' },
  secondary: { label: 'Project Overview',      to: '/researcher' },
};

export const RESEARCHER_FOOTER = {
  brandNote: 'Conceptualized, designed and developed by Dr. Keyur Sorathia & team at the Embedded Interaction Lab, Department of Design, IIT Guwahati.',
  cols: [
    { title: 'Research', items: [
      { label: 'Overview',            to: '/researcher' },
      { label: 'Research activities', to: '/researcher/activities' },
      { label: 'Evidence',            to: '/researcher/evidence' },
      { label: 'Collaboration',       to: '/researcher/collaborate' },
    ]},
    { title: 'Contact', items: [
      { label: 'keyur.sorathia@gyan-dhara.com', to: 'mailto:keyur.sorathia@gyan-dhara.com' },
    ]},
  ],
};

// 25 Researcher & University Overview — 85:3
export const RESEARCHER_OVERVIEW = {
  hero: {
    eyebrow: 'RESEARCH & COLLABORATION',
    title: 'A living research platform for immersive learning',
    desc:  'Gyan Dhara brings together design research, learning sciences, immersive technology and school-based evaluation to understand how students learn difficult concepts.',
    ctaPrimary:  { label: 'Explore Research Activities', to: '/researcher/activities' },
    ctaSecondary:{ label: 'Request Collaboration',      to: '/researcher/collaborate' },
    imageLabel:  'RESEARCH IN ACTION',
  },
  facts: [
    { k: 'HOME INSTITUTION', v: 'Dr. Keyur Sorathia & team at the Embedded Interaction Lab, Department of Design, IIT Guwahati' },
    { k: 'CURRENT CONTEXT',  v: 'Grade 8–9 Science and Mathematics' },
    { k: 'FIELD SETTING',    v: 'Schools in Assam' },
    { k: 'LANGUAGES',        v: 'English · Hindi · Assamese' },
  ],
  purpose: {
    eyebrow: 'RESEARCH PURPOSE',
    title:   'From product development to learning evidence',
    desc:    'The research programme studies not only whether students enjoy VR, but how immersive experiences influence understanding, mental models, application and retention.',
    items: [
      { title: 'Conceptual understanding', desc: 'How students form clearer explanations of difficult concepts.',        color: 'purple', icon: 'Brain' },
      { title: 'Mental-model clarity',      desc: 'How spatial and interactive representations shape internal understanding.', color: 'royal', icon: 'Cpu' },
      { title: 'Applied transfer',          desc: 'Whether learners can use knowledge in unfamiliar and real-world situations.', color: 'cyan', icon: 'ArrowUpRight' },
      { title: 'Learning experience',       desc: 'Engagement, usability, comfort and classroom feasibility.',            color: 'mint', icon: 'Sparkles' },
    ],
  },
  current: {
    eyebrow: 'CURRENT RESEARCH',
    title:   'Field evaluation underway',
    desc:    'A structured school study is examining learning outcomes, retention, usability and operational feasibility across selected curriculum modules.',
    studyEyebrow: 'ONGOING STUDY',
    studyTitle:   'Repeated-measures school evaluation',
    studyDesc:    'Students complete pre-learning, post-learning and retention assessments around immersive modules. Module order is counterbalanced to reduce sequence effects.',
    facts: [
      { k: 'PARTICIPANTS', v: 'Grade 8 and 9 learners' },
      { k: 'MODULES',       v: 'Sound · Combustion & Flame · Area 2D' },
      { k: 'MEASURES',      v: 'Conceptual · Applied · Theoretical' },
      { k: 'CONTEXT',       v: 'Two-school field study' },
    ],
    mediaEyebrow: 'FIELD TRIAL MEDIA',
  },
  approach: {
    eyebrow: 'RESEARCH APPROACH',
    title:   'Evidence across the learning journey',
    desc:    'Research activities connect classroom problems, co-design, immersive development and evaluation.',
    steps: [
      { n: 1, title: 'Identify learning difficulty', desc: 'Teacher consultation and classroom observation.',                       color: 'purple', icon: 'Search' },
      { n: 2, title: 'Design the experience',        desc: 'Learning outcomes, interaction and representation decisions.',           color: 'royal',  icon: 'Wand2' },
      { n: 3, title: 'Evaluate in context',          desc: 'Pre, post, retention and perception measures.',                          color: 'cyan',   icon: 'ClipboardCheck' },
      { n: 4, title: 'Translate findings',           desc: 'Design improvements, reports and responsible dissemination.',            color: 'mint',   icon: 'FileBarChart' },
    ],
  },
  collabAreas: {
    eyebrow: 'COLLABORATION AREAS',
    title:   'Where universities and researchers can contribute',
    desc:    'Collaboration can be scoped around a defined question, method, domain or deployment context.',
    items: [
      { title: 'Joint evaluation studies',     desc: 'Co-design evaluation protocols, instruments and analysis plans.',       color: 'purple', icon: 'Users' },
      { title: 'Learning sciences',            desc: 'Study mental models, conceptual change, transfer and retention.',       color: 'royal',  icon: 'BookOpen' },
      { title: 'HCI and accessibility',        desc: 'Usability, inclusive design, multilingual interaction and comfort.',    color: 'cyan',   icon: 'Accessibility' },
      { title: 'Data and analytics',           desc: 'Develop meaningful learning indicators and responsible reporting.',      color: 'mint',   icon: 'BarChart3' },
      { title: 'Thesis and student projects',  desc: 'Structured dissertation, internship or supervised research engagement.', color: 'purple', icon: 'GraduationCap' },
      { title: 'Ethics and responsible research', desc: 'Consent, privacy, governance and appropriate dissemination.',         color: 'royal',  icon: 'ShieldCheck' },
    ],
  },
  integrity: {
    eyebrow: 'RESEARCH INTEGRITY',
    title:   'Responsible evidence before strong claims',
    desc:    'Gyan Dhara distinguishes between ongoing evaluation, preliminary observations and validated findings.',
    principles: [
      { title: 'Consent and permissions', desc: 'Research participation follows school and parent or guardian requirements.',   color: 'purple', icon: 'FileCheck' },
      { title: 'Restricted data access',  desc: 'Research data is accessed only by authorised researchers.',                    color: 'royal',  icon: 'Lock' },
      { title: 'Responsible reporting',   desc: 'Identifiable information is not published without consent.',                    color: 'cyan',   icon: 'FileText' },
      { title: 'Evidence status',         desc: 'Preliminary findings are clearly separated from completed analysis.',          color: 'mint',   icon: 'BadgeCheck' },
    ],
  },
  portfolio: {
    eyebrow: 'LEARNING MODULE PORTFOLIO',
    title:   'Modules available for study and evaluation',
    desc:    'Current modules can support learning-science, HCI, usability and field-evaluation studies. Upcoming modules expand the research pipeline.',
    available: [
      { title: 'Sound',                   subject: 'Science',      status: 'Available' },
      { title: 'Combustion & Flame',      subject: 'Science',      status: 'Available' },
      { title: "Gravity's Grip",          subject: 'Science',      status: 'Available' },
      { title: 'Friction',                 subject: 'Science',      status: 'Available' },
      { title: 'Work',                     subject: 'Science',      status: 'Available' },
      { title: 'Energy',                   subject: 'Science',      status: 'Available' },
      { title: 'Falltopia',                subject: 'Science',      status: 'Available' },
      { title: 'AeroQuest',                subject: 'Science',      status: 'Available' },
      { title: 'Human Excretory System',   subject: 'Science',      status: 'Available' },
      { title: 'Area 2D',                  subject: 'Mathematics',  status: 'Available' },
      { title: 'Area 3D',                  subject: 'Mathematics',  status: 'Available' },
      { title: 'Volume',                   subject: 'Mathematics',  status: 'Available' },
    ],
    upcoming: [
      { title: 'Structure of Atoms',           subject: 'Science' },
      { title: 'Area of Cone',                 subject: 'Mathematics' },
      { title: 'Area of Sphere',               subject: 'Mathematics' },
      { title: 'Volume of Sphere and Cone',    subject: 'Mathematics' },
      { title: 'Contact & Non-contact Forces', subject: 'Science' },
      { title: 'Light',                        subject: 'Science' },
    ],
  },
  cta: {
    title: 'Bring a research question, method or academic partnership.',
    desc:  'The collaboration request helps the team understand the proposed scope, institution, timeline and expected contribution.',
    primary:   { label: 'Request Collaboration', to: '/researcher/collaborate' },
  },
};

// 26 Research Activities & Evidence — 86:2
export const RESEARCHER_ACTIVITIES = {
  hero: {
    eyebrow: 'RESEARCH ACTIVITIES',
    title:   'From classroom questions to evidence',
    desc:    'Research activities are organised around learner needs, responsible evaluation and iterative improvement of the immersive experience.',
    flowLabel: 'QUESTION  →  STUDY  →  EVIDENCE',
    flowDesc:  'Research is treated as a cycle of inquiry, evaluation and design improvement.',
    cta: { label: 'Request Research Summary', to: '/researcher/collaborate' },
  },
  programme: {
    eyebrow: 'RESEARCH PROGRAMME',
    title:   'Four connected activity streams',
    desc:    'Each stream informs both academic inquiry and product decisions.',
    streams: [
      { n: '01', title: 'Learning challenges', desc: 'Teacher workshops, classroom conversations and curriculum analysis identify difficult concepts.', color: 'purple' },
      { n: '02', title: 'Experience design',   desc: 'Learning outcomes, representations, interactions and language are developed and reviewed.',         color: 'royal' },
      { n: '03', title: 'Field evaluation',    desc: 'Learners participate in supervised studies of learning, retention, usability and feasibility.',     color: 'cyan' },
      { n: '04', title: 'Design translation',  desc: 'Findings inform module revisions, deployment protocols and future research questions.',              color: 'mint' },
    ],
  },
  focus: {
    eyebrow: 'EVALUATION FOCUS',
    title:   'What is being examined',
    desc:    'The programme combines learning outcomes with experience and implementation measures.',
    items: [
      { key: 'CU', title: 'Conceptual understanding', desc: 'Explanations, relationships and conceptual accuracy.',        color: 'purple' },
      { key: 'MM', title: 'Mental models',             desc: 'How learners imagine and organise invisible or abstract processes.', color: 'royal' },
      { key: 'AT', title: 'Applied transfer',          desc: 'Use of knowledge in real-world and unfamiliar situations.',   color: 'cyan' },
      { key: 'RT', title: 'Retention',                 desc: 'What learners remember after a delayed interval.',            color: 'mint' },
      { key: 'UX', title: 'Usability and comfort',     desc: 'Navigation, interaction, confidence and VR comfort.',         color: 'purple' },
      { key: 'OF', title: 'Operational feasibility',   desc: 'Scheduling, device readiness and support in real settings.',  color: 'royal' },
    ],
  },
  status: {
    eyebrow: 'CURRENT EVIDENCE STATUS',
    title:   'Field evaluation is underway',
    desc:    'The website should communicate what is known without presenting incomplete analysis as a final claim.',
    rows: [
      { label: 'COMPLETED',       desc: 'Teacher co-design, module development and usability-informed iteration.', tone: 'mint' },
      { label: 'UNDERWAY',        desc: 'Structured evaluation of learning, retention and experience.',              tone: 'royal' },
      { label: 'PRELIMINARY',     desc: 'Early patterns can be shared with clear limitations and context.',          tone: 'cyan' },
      { label: 'NOT YET CLAIMED', desc: 'Final comparative claims and generalisable conclusions.',                    tone: 'purple' },
    ],
  },
  lifecycle: {
    eyebrow: 'RESEARCH LIFECYCLE',
    title:   'How a study moves forward',
    desc:    'Collaboration begins with a defined question and an agreed ethical and operational pathway.',
    steps: [
      { n: 1, title: 'Define the question',    desc: 'Clarify constructs, participants and expected contribution.',       color: 'purple' },
      { n: 2, title: 'Agree the protocol',     desc: 'Methods, instruments, permissions and responsibilities.',            color: 'royal' },
      { n: 3, title: 'Conduct the study',      desc: 'Supervised implementation and structured data collection.',           color: 'cyan' },
      { n: 4, title: 'Analyse and interpret',  desc: 'Transparent analysis with limitations and context.',                  color: 'mint' },
      { n: 5, title: 'Improve and disseminate', desc: 'Design recommendations, reporting and agreed publication.',           color: 'purple' },
    ],
  },
  resources: {
    eyebrow: 'RESOURCES',
    title:   'Research material available on request',
    desc:    'Material can be shared according to study status, agreements and intended use.',
    items: [
      { key: 'R', title: 'Project research overview',  desc: 'Purpose, questions and activity streams.',            color: 'purple' },
      { key: 'M', title: 'Methodology summary',         desc: 'Study design, measures and implementation context.',  color: 'royal' },
      { key: 'P', title: 'Preliminary findings brief',  desc: 'Responsible summary when suitable for sharing.',      color: 'cyan' },
      { key: 'C', title: 'Collaboration concept note',  desc: 'Scope, responsibilities and proposed pathway.',       color: 'mint' },
    ],
  },
  cta: {
    title: 'Discuss a research question or request an appropriate summary.',
    primary: { label: 'Request Collaboration', to: '/researcher/collaborate' },
  },
};

// 27 Evidence Coming Soon — 93:2
export const RESEARCHER_EVIDENCE = {
  hero: {
    eyebrow: 'EVIDENCE',
    title:   'Research evidence is coming soon',
    desc:    'Field evaluation and analysis are underway. This page will publish research findings only after the study, analysis and responsible review are complete.',
    badge:   'ANALYSIS UNDERWAY',
    cta: { label: 'Request Collaboration', to: '/researcher/collaborate' },
  },
  status: {
    eyebrow: 'CURRENT STATUS',
    title:   'What is happening now',
    desc:    'The evidence programme is progressing through structured data collection, analysis and responsible interpretation.',
    steps: [
      { n: 1, title: 'Field evaluation',  desc: 'Pre-learning, post-learning and retention measures are being collected.',           color: 'purple' },
      { n: 2, title: 'Data validation',   desc: 'Records are being checked for completeness, quality and appropriate interpretation.', color: 'royal' },
      { n: 3, title: 'Analysis',          desc: 'Conceptual, applied, theoretical, usability and retention measures will be analysed.', color: 'cyan' },
      { n: 4, title: 'Research review',   desc: 'Findings will be reviewed with limitations, context and ethical requirements.',       color: 'mint' },
    ],
  },
  planned: {
    eyebrow: 'WHAT WILL APPEAR HERE',
    title:   'Evidence planned for publication',
    desc:    'The final page will communicate findings in a form that is useful to researchers, universities and education partners.',
    items: [
      { key: 'LO', title: 'Learning outcomes',       desc: 'Changes in conceptual, theoretical and applied understanding.', color: 'purple' },
      { key: 'RT', title: 'Retention',                desc: 'Delayed learning measures across selected modules.',           color: 'royal' },
      { key: 'UX', title: 'Learner experience',       desc: 'Engagement, usability, comfort and willingness to reuse.',     color: 'cyan' },
      { key: 'OF', title: 'Operational feasibility',  desc: 'Scheduling, device readiness and implementation observations.', color: 'mint' },
      { key: 'MT', title: 'Methodology',              desc: 'Participants, study design, measures and analytical approach.', color: 'purple' },
      { key: 'LM', title: 'Limitations',              desc: 'Scope, context and boundaries of interpretation.',              color: 'royal' },
    ],
  },
  principles: {
    eyebrow: 'EVIDENCE PRINCIPLES',
    title:   'How findings will be communicated',
    desc:    'Gyan Dhara will prioritise clarity, transparency and appropriate claims over premature conclusions.',
    items: [
      { icon: 'CheckCircle2', title: 'No premature claims',   desc: 'Preliminary observations will not be presented as final evidence.', color: 'mint' },
      { icon: 'AlertTriangle', title: 'No overgeneralisation', desc: 'Findings will remain tied to the study context and participant group.', color: 'purple' },
      { icon: 'Info',          title: 'Methods included',      desc: 'Study design, measures and limitations will accompany the results.', color: 'royal' },
      { icon: 'ShieldCheck',   title: 'Privacy protected',     desc: 'Identifiable participant information will not be publicly disclosed.', color: 'cyan' },
    ],
  },
  pathway: {
    eyebrow: 'PUBLICATION PATHWAY',
    title:   'From fieldwork to public evidence',
    desc:    'The evidence page will be updated only after each stage is complete.',
    steps: [
      { n: 1, title: 'Complete data collection', desc: 'Finish scheduled post-learning and retention activities.',  color: 'purple' },
      { n: 2, title: 'Validate and analyse',      desc: 'Check data quality and complete the agreed analysis.',      color: 'royal' },
      { n: 3, title: 'Interpret responsibly',     desc: 'Review findings, limitations and contextual factors.',      color: 'cyan' },
      { n: 4, title: 'Prepare research summary',  desc: 'Create a clear, non-technical overview with methodology.',  color: 'mint' },
      { n: 5, title: 'Publish evidence',          desc: 'Release approved findings and supporting research material.', color: 'purple' },
    ],
  },
  cta: {
    title: 'Interested in contributing before the evidence is published?',
    desc:  'Researchers and universities can propose an evaluation, analysis, learning-science or HCI collaboration while the programme is underway.',
    footnote: 'Evidence will be added here when the analysis and review are complete.',
    primary: { label: 'Request Collaboration', to: '/researcher/collaborate' },
  },
};

// 28 Research Collaboration Request — 87:2
export const RESEARCHER_COLLABORATE = {
  hero: {
    eyebrow: 'COLLABORATE',
    title:   'Propose a research collaboration',
    desc:    'Share the research question, proposed contribution and institutional context. The Gyan Dhara team will review the request and respond to discuss feasibility and next steps.',
    tipsTitle: 'A useful request includes',
    tips: [
      'A focused research question',
      'Relevant method or expertise',
      'Expected academic contribution',
      'Indicative timeline and responsibilities',
    ],
  },
  formTitle: 'Collaboration request',
  formHint:  'The form is intended for researchers, faculty members, universities and supervised student researchers.',
  interests: ['Joint evaluation study', 'Learning sciences research', 'HCI, UX or accessibility', 'Data and analytics', 'Thesis or student project', 'Other academic collaboration'],
  readiness: [
    'A research question has been defined.',
    'Institutional or ethics requirements can be discussed.',
    'I understand that collaboration scope and data access require a written agreement.',
  ],
  submit: 'Submit Collaboration Request',
  submitNote: 'The request will be sent to keyur.sorathia@gyan-dhara.com',
  privacyNote: 'Information is used only to review and respond to the proposed collaboration.',
  nextTitle: 'What happens next',
  next: [
    { n: 1, title: 'Request review',       desc: 'Research question and institutional context' },
    { n: 2, title: 'Initial discussion',   desc: 'Fit, feasibility and expected contribution' },
    { n: 3, title: 'Scope agreement',      desc: 'Method, roles, ethics, data and timeline' },
    { n: 4, title: 'Collaboration begins', desc: 'After the agreed documentation is completed' },
  ],
  beforeTitle: 'Before submitting',
  beforeItems: [
    { key: 'RQ',  title: 'Focused question',  desc: 'Avoid proposals that are too broad.' },
    { key: 'FIT', title: 'Relevant fit',      desc: 'Connect the proposal to immersive learning.' },
    { key: 'ETH', title: 'Research ethics',   desc: 'Expect consent and governance discussion.' },
    { key: 'OUT', title: 'Expected output',   desc: 'Clarify the academic or design contribution.' },
  ],
  confirmation: {
    title: 'Request received',
    body:  'Thank you. The Gyan Dhara team will review the request and contact you to discuss the proposed collaboration.',
    cta:   { label: 'Return to Research Overview', to: '/researcher' },
  },
};
